import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { hasSeenOnboarding, getStudyStreak, getLearnedWords } from '../utils/storage';
import OnboardingScreen from './OnboardingScreen';
import Icon from '../components/Icon';
import './styles/WelcomeScreen.css';

const WelcomeScreen = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showOnboarding, setShowOnboarding] = useState(() => !hasSeenOnboarding());
  const streak = getStudyStreak();
  const learnedCount = getLearnedWords().length;

  const sections = [
    { title: 'Sanastoa', subtitle: t('vocabulary'), icon: 'book', color: 'var(--color-vocab)', bgColor: 'var(--color-vocab-dim)', path: '/vocabulary' },
    { title: 'Kirjoittaminen', subtitle: t('writing'), icon: 'pen', color: 'var(--color-writing)', bgColor: 'var(--color-writing-dim)', path: '/writing' },
    { title: 'Puhuminen', subtitle: t('speaking'), icon: 'mic', color: 'var(--color-speaking)', bgColor: 'var(--color-speaking-dim)', path: '/speaking' },
  ];

  return (
    <div className="container">
      <Helmet>
        <title>YKI Testi - Free Finnish Language Test Practice</title>
        <meta name="description" content="Practice for the YKI Finnish language proficiency test for free. Vocabulary flashcards, writing exercises, speaking practice and quizzes for A1–B2 levels." />
        <link rel="canonical" href="https://ykitesti.fi/" />
      </Helmet>

      {showOnboarding && <OnboardingScreen onComplete={() => setShowOnboarding(false)} />}

      {/* Hero */}
      <div className="welcome-hero">
        <img src={require('../assets/icons/logo.png')} alt="YKI Testi" className="logo" />
        <p className="welcome-tagline">{t('welcomeDescription')}</p>
      </div>

      {/* Streak Banner */}
      <button className="streak-banner" onClick={() => navigate('/progress')}>
        <div className="streak-left">
          <span className="streak-fire"><Icon name="flame" size={30} color="var(--color-progress)" /></span>
          <div className="streak-text">
            <span className="streak-count">{streak.count} {t('dayStreak')}</span>
            <span className="streak-sub">{streak.count > 0 ? t('keepItGoing') : t('startLearningToday')}</span>
          </div>
        </div>
        <button
          className="streak-words-chip"
          onClick={(e) => {
            e.stopPropagation();
            navigate('/learned-words');
          }}
        >
          <span className="streak-words-count">{learnedCount}</span>
          <span className="streak-words-label">{t('wordsLearned')}</span>
        </button>
      </button>

      {/* Section Cards */}
      <div className="section-cards">
        {sections.map((section, index) => (
          <button className="section-card" key={index} onClick={() => navigate(section.path)}>
            <div className="section-icon-container" style={{ backgroundColor: section.bgColor }}>
              <span className="section-icon" style={{ color: section.color }}><Icon name={section.icon} size={24} /></span>
            </div>
            <div className="section-card-text">
              <span className="section-card-title" style={{ color: section.color }}>{section.title}</span>
              <span className="section-card-subtitle">({section.subtitle})</span>
            </div>
            <span className="section-card-arrow">&rsaquo;</span>
          </button>
        ))}

        {/* Quiz Card */}
        <button className="section-card" onClick={() => navigate('/test-setting-screen')}>
          <div className="section-icon-container" style={{ backgroundColor: 'var(--color-error-dim)' }}>
            <span className="section-icon" style={{ color: 'var(--color-error)' }}><Icon name="target" size={24} /></span>
          </div>
          <div className="section-card-text">
            <span className="section-card-title" style={{ color: 'var(--color-error)' }}>Testi</span>
            <span className="section-card-subtitle">({t('testVocabulary')})</span>
          </div>
          <span className="section-card-arrow">&rsaquo;</span>
        </button>

        {/* Progress Card */}
        <button className="section-card" onClick={() => navigate('/progress')}>
          <div className="section-icon-container" style={{ backgroundColor: 'var(--color-progress-dim)' }}>
            <span className="section-icon" style={{ color: 'var(--color-progress)' }}><Icon name="chart" size={24} /></span>
          </div>
          <div className="section-card-text">
            <span className="section-card-title" style={{ color: 'var(--color-progress)' }}>Edistyminen</span>
            <span className="section-card-subtitle">({t('progress')})</span>
          </div>
          <span className="section-card-arrow">&rsaquo;</span>
        </button>
      </div>

      {/* Resource Links */}
      <div className="resource-cards">
        <Link to="/yki-guide" className="resource-card">
          <span className="resource-card-icon"><Icon name="clipboard" size={22} /></span>
          <div className="resource-card-text">
            <span className="resource-card-title">{t('ykiGuide')}</span>
            <span className="resource-card-desc">{t('ykiGuideDesc')}</span>
          </div>
        </Link>
        <Link to="/study-tips" className="resource-card">
          <span className="resource-card-icon"><Icon name="bulb" size={22} /></span>
          <div className="resource-card-text">
            <span className="resource-card-title">{t('studyTips')}</span>
            <span className="resource-card-desc">{t('studyTipsDesc')}</span>
          </div>
        </Link>
        <Link to="/faq" className="resource-card">
          <span className="resource-card-icon"><Icon name="help" size={22} /></span>
          <div className="resource-card-text">
            <span className="resource-card-title">{t('faq')}</span>
            <span className="resource-card-desc">{t('faqDesc')}</span>
          </div>
        </Link>
      </div>

      {/* App Download */}
      <div className="app-links">
        <div className="app-links-row1">{t('downloadApp')}</div>
        <div className="app-links-row2">
          <a href="https://apps.apple.com/fi/app/yki-test/id6468961028" target="_blank" rel="noreferrer">
            <img src={require('../assets/images/app-logo-store.png')} alt="Download on the App Store" className="store-badge" />
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.ykitesti.app" target="_blank" rel="noreferrer">
            <img src={require('../assets/images/android-logo.png')} alt="Get it on Google Play" className="store-badge" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
